
import { XsectTypeWords } from "@fileops/swmm-node"

/**
 * Test format for swmmInp format for generic input interfaces.
 * 
 * Attributes with names ending in *Link are links to another table.
 * The 'otherTables' attribute is an extension of the named section in the array.
 *   - XSECTIONS should not be part of 'otherTables', it should be a link. 
 */
const SwmmInpFormat = {
  //==========POLLUTANTS==============
  POLLUTANTS: {
    Units: {
      label: "Units",
      tooltip: "Concentration units for the pollutant",
      input: "UniversalDropDown",
      entries: ['MG/L', 'UG/L', '#/L'],
      default: 'MG/L'
    },
    Cppt: {
      label: "Rain Concen.",
      tooltip: "Concentration of the pollutant in rain water",
      input: "UniversalInput",
      default: '0'
    },
    Cgw: {
      label: "GW Concen.",
      tooltip: "Concentration of the pollutant in ground water",
      input: "UniversalInput",
      default: '0'
    },
    Crdii: {
      label: "I&I Concen.",
      tooltip: "Concentration of the pollutant in infiltration/inflow flow",
      input: "UniversalInput",
      default: '0'
    },
    Cdwf: {
      label: "DWF Concen.",
      tooltip: "Concentration of the pollutant in dry weather sanitary flow",
      input: "UniversalInput",
      default: '0'
    },
    Cinit: {
      label: "Init Concen.",
      tooltip: "Initial concentration of the pollutant throughout the conveyance system",
      input: "UniversalInput",
      default: '0'
    },
    Kdecay: {
      label: "Decay Coeff.",
      tooltip: "First-order decay coefficient of the pollutant (1/days)",
      input: "UniversalInput",
      default: '0'
    },
    SnowOnly: {
      label: "Snow Only",
      tooltip: "Does the pollutant build up only during snowfall events?",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'NO'
    },
    CoPollutant: {
      label: "Co-Pollutant",
      tooltip: "Name of another pollutant to which runoff concentration the current pollutant is dependent on",
      input: "UniversalInput",
      default: ''
    },
    CoFrac: {
      label: "Co-Fraction",
      tooltip: "Fraction of the co-pollutant's runoff concentration that becomes the current pollutant's runoff concentration",
      input: "UniversalInput",
      default: ''
    },
  },
  //==========AQUIFERS==============
  AQUIFERS: {
    Por: {
      label: "Porosity",
      tooltip: "Volume of voids / total soil volume",
      input: "UniversalInput",
      default: '0.5'
    },
    WP: {
      label: "Wilting Point",
      tooltip: "Residual moisture content of a completely dry soil (fraction)",
      input: "UniversalInput",
      default: '0.15'
    },
    FC: {
      label: "Field Capacity",
      tooltip: "Soil moisture content after all free water has drained off (fraction)",
      input: "UniversalInput",
      default: '0.3'
    },
    Ks: {
      label: "Conductivity",
      tooltip: "Soil's saturated hydraulic conductivity (in/hr or mm/hr)",
      input: "UniversalInput",
      default: '5.0'
    },
    Kslp: {
      label: "Conductivity Slope",
      tooltip: "Slope of log(conductivity) v. soil moisture deficit curve",
      input: "UniversalInput",
      default: '10.0'
    },
    Tslp: {
      label: "Tension Slope",
      tooltip: "Slope of soil tension v. soil moisture content curve",
      input: "UniversalInput",
      default: '15.0'
    },
    ETu: {
      label: "Upper Evap. Fraction",
      tooltip: "Fraction of total evaporation available for upper unsaturated zone",
      input: "UniversalInput",
      default: '0.35'
    },
    ETs: {
      label: "Lower Evap. Depth",
      tooltip: "Depth into saturated zone over which evaporation can occur (ft or m)",
      input: "UniversalInput",
      default: '14.0'
    },
    Seep: {
      label: "Lower GW Loss Rate",
      tooltip: "Rate of seepage to deep groundwater when aquifer is completely saturated (in/hr or mm/hr)",
      input: "UniversalInput",
      default: '0.002'
    },
    Ebot: {
      label: "Bottom Elevation",
      tooltip: "Elevation of the bottom of the aquifer (ft or m)",
      input: "UniversalInput",
      default: '0.0'
    },
    Egw: {
      label: "Water Table Elevation",
      tooltip: "Initial water table elevation (ft or m)",
      input: "UniversalInput",
      default: '10.0'
    },
    Umc: {
      label: "Unsat. Zone Moisture",
      tooltip: "Initial moisture content of the unsaturated upper zone (fraction)",
      input: "UniversalInput",
      default: '0.3'
    },
    Epat: {
      label: "Upper Evap. Pattern",
      tooltip: "Monthly pattern of adjustments to upper evaporation fraction (optional)",
      input: "UniversalInput",
      default: ''
    },
  },
  //==========DIVIDERS==============
  DIVIDERS: {
    Invert: {
      label: "Invert El.",
      tooltip: "Elevation of divider's invert",
      input: "UniversalInput",
      default: '0'
    },
    Dmax: {
      label: "Max. Depth",
      tooltip: "Maximum water depth (i.e., distance from invert to ground surface). Enter 0 to use distance from invert to top of highest connecting link.",
      input: "UniversalInput",
      default: '0'
    },
    Dinit: {
      label: "Initial Depth",
      tooltip: "Initial water depth in junction",
      input: "UniversalInput",
      default: '0'
    },
    Dsurch: {
      label: "Surcharge Depth",
      tooltip: "Depth in excess of maximum depth before flooding occurs",
      input: "UniversalInput",
      default: '0'
    },
    Aponded: {
      label: "Ponded Area",
      tooltip: "Area of ponded water when flooded.",
      input: "UniversalInput",
      default: '0'
    },
    DivLink: {
      label: "Diverted Link",
      tooltip: "Name of link which receives the diverted flow.",
      input: "UniversalInput",
      default: '*'
    },
    Type: {
      label: "Type",
      tooltip: "Type of flow divider",
      input: "UniversalDropDown",
      entries: ['WEIR', 'CUTOFF', 'TABULAR', 'OVERFLOW'],
      default: '*'
    },
    Qmax: {
      label: "Cutoff Flow",
      tooltip: "Cutoff flow value used for a CUTOFF divider (flow units)",
      input: "UniversalInput",
      default: '0'
    },
    Dcurve: {
      label: "Curve Name",
      tooltip: "Name of diversion curve used with a TABULAR divider.",
      input: "UniversalDropDown",
      entries: 'CURVES',
      section: ['Diversion'],
      default: '*'
    },
    Qmin: {
      label: "Min. Flow",
      tooltip: "Minimum flow at which diversion begins for a WEIR divider.",
      input: "UniversalInput",
      default: '0'
    },
    Ht: {
      label: "Max. Depth",
      tooltip: "Depth at maximum flow for a WEIR divider",
      input: "UniversalInput",
      default: '0'
    },
    Cd: {
      label: "Coefficient",
      tooltip: "Discharge coefficient for a WEIR divider",
      input: "UniversalInput",
      default: '0'
    },
    InflowsLink: {
      label: "Inflows",
      tooltip: "Node inflows",
      input: "Button",
      clickEffect: 'Inflows',
      default: ''
    },
    TreatmentLink: {
      label: "Treatment",
      tooltip: "Node treatment",
      input: "Button",
      clickEffect: 'Treatment',
      default: ''
    },
    Description: {
      label: "Description",
      tooltip: "Optional comment or description",
      input: "UniversalInput",
      default: ''
    }
  }, 
  //==========OUTFALLS===============
  OUTFALLS: {
    Invert: {
      label: "Invert El.",
      tooltip: "Elevation of outfall's invert",
      input: "UniversalInput",
      default: '0'
    },
    Gated: {
      label: "Tide Gate",
      tooltip: "Select YES of outfall contains a tide gate to prevent backflow",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'NO'
    },
    RouteTo: {
      label: "Route To",
      tooltip: "Subcatchment outflow is routed onto (leave blank if not applicable)",
      input: "UniversalInput",
      default: ''
    },
    Type: {
      label: "Type",
      tooltip: "Type of outfall boundary condition",
      input: "UniversalDropDown",
      entries: ['FREE', 'NORMAL', 'FIXED', 'TIDAL', 'TIMESERIES'],
      default: 'FREE'
    },
    StageData: {
      label: "Fixed Stage",
      tooltip: "Water elevation for a FIXED type of boundary condition",
      input: "UniversalInput",
      default: '0'
    },
    Tcurve: {
      label: "Curve Name",
      tooltip: "Name of tidal curve used for a TIDAL boundary condition",
      input: "UniversalDropDown",
      entries: 'CURVES',
      section: ['Tidal'],
      default: '*'
    },
    Tseries: {
      label: "Time Series Name",
      tooltip: "Name of time series for a TIMESERIES boundary condition",
      input: "UniversalDropDown",
      entries: 'TIMESERIES',
      section: [],
      default: '*'
    },
    InflowsLink: {
      label: "Inflows",
      tooltip: "Node inflows",
      input: "Button",
      clickEffect: 'Inflows',
      default: ''
    },
    TreatmentLink: {
      label: "Treatment",
      tooltip: "Node treatment",
      input: "Button",
      clickEffect: 'Treatment',
      default: ''
    },
    Description: {
      label: "Description",
      tooltip: "Optional comment or description",
      input: "UniversalInput",
      default: ''
    }
  },
  //==========STORAGE===============
  STORAGE: {
    Invert: {
      label: "Invert El.",
      tooltip: "Elevation of the bottom of the storage unit",
      input: "UniversalInput",
      default: '0'
    },
    Dmax: {
      label: "Max. Depth",
      tooltip: "Maximum depth of the storage unit",
      input: "UniversalInput",
      default: '0'
    },
    Dinit: {
      label: "Initial Depth",
      tooltip: "Initial depth of the water in the storage unit",
      input: "UniversalInput",
      default: '0'
    },
    Dsurch: {
      label: "Surcharge Depth",
      tooltip: "Depth in excess of maximum depth before flooding occurs",
      input: "UniversalInput",
      default: '0'
    },
    Fevap: {
      label: "Evap. Factor",
      tooltip: "Fraction of evaporation rate realized (e.g.: enter 1 for full evaporation or 0 for no evaporation)",
      input: "UniversalInput",
      default: '0'
    },
    PSI: {
      label: "Suction Head",
      tooltip: "Capillary suction head (inches or mm)",
      input: "UniversalInput",
      default: '0'
    },
    Ksat: {
      label: "Conductivity",
      tooltip: "Saturated hydraulic conductivity (in/hr or mm/hr). Enter 0 for no seepage.",
      input: "UniversalInput",
      default: '0'
    },
    IMD: {
      label: "Initial Moisture Deficit",
      tooltip: "Difference between porosity and initial moisture content. Enter 0 for constant seepage equal to conductivity.",
      input: "UniversalInput",
      default: '0'
    },
    Curve: {
      label: "Storage Shape",
      tooltip: "Method of describing the geometric shape of the storage unit",
      input: "UniversalDropDown",
      entries: ['CYLINDRICAL', 'CONICAL', 'PARABOLIC', 'PYRAMIDAL', 'FUNCTIONAL', 'TABULAR'],
      default: 'CYLINDRICAL'
    },
    P1: {
      label: "Parameter 1",
      tooltip: "First parameter for Storage Shape (base length or area)",
      input: "UniversalInput",
      default: '0'
    },
    P2: {
      label: "Parameter 2",
      tooltip: "Height, width, or functional coefficient",
      input: "UniversalInput",
      default: '0'
    },
    P3: {
      label: "Parameter 3",
      tooltip: "Slope or functional exponent",
      input: "UniversalInput",
      default: '0'
    },
    CurveName: {
      label: "Curve Name",
      tooltip: "",
      input: "UniversalDropDown",
      entries: 'CURVES',
      section: ['Storage'],
      default: '*'
    },
    InflowsLink: {
      label: "Inflows",
      tooltip: "Node inflows",
      input: "Button",
      clickEffect: 'Inflows',
      default: ''
    },
    TreatmentLink: {
      label: "Treatment",
      tooltip: "Node treatment",
      input: "Button",
      clickEffect: 'Treatment',
      default: ''
    },
    Description: {
      label: "Description",
      tooltip: "Optional name of curve table for paired depth and surface area.",
      input: "UniversalInput",
      default: ''
    },
  },
  //==========JUNCTIONS==============
  JUNCTIONS: {
    Invert: {
      label: "Invert El.",
      tooltip: "Elevation of junction's invert",
      input: "UniversalInput",
      default: '0'
    },
    Dmax: {
      label: "Max. Depth",
      tooltip: "Maximum water depth (i.e., distance from invert to ground surface). Enter 0 to use distance from invert to top of highest connecting link.",
      input: "UniversalInput",
      default: '0'
    },
    Dinit: {
      label: "Initial Depth",
      tooltip: "Initial water depth in junction.",
      input: "UniversalInput",
      default: '0'
    },
    Dsurch: {
      label: "Surcharge Depth",
      tooltip: "Depth in excess of maximum depth before flooding occurs.",
      input: "UniversalInput",
      default: '0'
    },
    Aponded: {
      label: "Ponded Area",
      tooltip: "Area of ponded water when flooded.",
      input: "UniversalInput",
      default: '0'
    },
    InflowsLink: {
      label: "Inflows",
      tooltip: "Node inflows",
      input: "Button",
      clickEffect: 'Inflows',
      default: ''
    },
    TreatmentLink: {
      label: "Treatment",
      tooltip: "Node treatment",
      input: "Button",
      clickEffect: 'Treatment',
      default: ''
    },
  },
  //==========CONDUITS==============
  CONDUITS: {
    Node1: {
      label: "Inlet Node",
      tooltip: "Name of node on the inlet end of conduit",
      input: "UniversalInput",
      default: '0'
    },
    Node2: {
      label: "Outlet Node",
      tooltip: "Name of node on the outlet end of conduit",
      input: "UniversalInput",
      default: '0'
    },
    Length: {
      label: "Length",
      tooltip: "Conduit length",
      input: "UniversalInput",
      default: '0'
    },
    Roughness: {
      label: "Roughness",
      tooltip: "Manning's roughness coefficient",
      input: "UniversalInput",
      default: '0'
    },
    InOffset: {
      label: "Inlet Offset",
      tooltip: "Depth of conduit invert above node invert at inlet end",
      input: "UniversalInput",
      default: '0'
    },
    OutOffset: {
      label: "Outlet Offset",
      tooltip: "Depth of conduit invert above node invert at outlet end",
      input: "UniversalInput",
      default: '0'
    },
    InitFlow: {
      label: "Initial Flow",
      tooltip: "Initial flow in the conduit",
      input: "UniversalInput",
      default: '0'
    },
    MaxFlow: {
      label: "Maximum Flow",
      tooltip: "Maximum flow allowed - use 0 if not applicable",
      input: "UniversalInput",
      default: '0'
    },
    ShapeLink: {
      label: "Shape",
      tooltip: "Conduit shape",
      input: "Button",
      clickEffect: 'Shape',
      default: ''
    },
    Description: {
      label: "Description",
      tooltip: "Optional comment or description",
      input: "UniversalInput",
      default: '0'
    },
    otherTables: ['LOSSES']
  },
  //==========PUMPS==============
  PUMPS: {
    Node1: {
      label: "Inlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Node2: {
      label: "Outlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Pcurve: {
      label: "Pump Curve",
      tooltip: "",
      input: "UniversalDropDown",
      entries: 'CURVES',
      section: ['Pump1', 'Pump2', 'Pump3', 'Pump4', 'Pump5'],
      default: '*'
    },
    Status: {
      label: "Initial Status",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['ON', 'OFF'],
      default: 'ON'
    },
    Startup: {
      label: "Startup Depth",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Shutoff: {
      label: "Shutoff Depth",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Description: {
      label: "Description",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    }
  },
  //==========OUTLETS==============
  OUTLETS: {
    Node1: {
      label: "Inlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Node2: {
      label: "Outlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Offset: {
      label: "Inlet Offset",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Gated: {
      label: "Flap Gate",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'NO'
    },
    Type: {
      label: "Rating Curve",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['FUNCTIONAL/DEPTH', 'TABULAR/DEPTH', 'FUNCTIONAL/HEAD', 'TABULAR/HEAD'],
      default: 'FUNCTIONAL/DEPTH'
    },
    C1: {
      label: "Coefficient",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    C2: {
      label: "Exponent",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Curve: {
      label: "Curve Name",
      tooltip: "",
      input: "UniversalDropDown",
      entries: 'CURVES',
      section: ['Rating'],
      default: '*'
    },
    Description: {
      label: "Description",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    }
  },
  //==========RAINGAGES==============
  RAINGAGES: {
    Format: {
      label: "Rain Format",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['INTENSITY', 'VOLUME', 'CUMULATIVE'],
      default: 'INTENSITY'
    },
    Interval: {
      label: "Time Interval",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    SCF: {
      label: "Snow Catch Factor",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Source: {
      label: "Data Source",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['TIMESERIES', 'FILE'],
      default: 'TIMESERIES'
    },
    SeriesName: {
      label: "Time Series Name",
      tooltip: "",
      input: "UniversalDropDown",
      entries: 'TIMESERIES',
      section: [],
      default: '*'
    },
    Description: {
      label: "Description",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    }
  },
  //==========ORIFICES==============
  ORIFICES: {
    Node1: {
      label: "Inlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Node2: {
      label: "Outlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Type: {
      label: "Type",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['SIDE', 'BOTTOM'],
      default: 'SIDE'
    },
    Offset: {
      label: "Inlet ffset",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Cd: {
      label: "Discharge Coeff.",
      tooltip: "",
      input: "UniversalInput",
      default: '0.65'
    },
    Gated: {
      label: "Flap Gate",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'NO'
    },
    Orate: {
      label: "Time to Open/Close",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Description: {
      label: "Description",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    otherTables: ['XSECTIONS']
  },
  //==========WEIRS==============
  WEIRS: {
    Node1: {
      label: "Inlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Node2: {
      label: "Outlet Node",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Type: {
      label: "Type",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['TRANSVERSE', 'SIDEFLOW', 'v-NOTCH', 'TRAPEZOIDAL', 'ROADWAY'],
      default: 'TRANSVERSE'
    },
    CrestHt: {
      label: "Inlet Offset",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Cd: {
      label: "Discharge Coeff.",
      tooltip: "",
      input: "UniversalInput",
      default: '3.33'
    },
    Gated: {
      label: "Flap Gate",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'NO'
    },
    EC: {
      label: "End Contractions",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Cd2: {
      label: "End Coeff.",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Surcharge: {
      label: "Can Surcharge",
      tooltip: "",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'YES'
    },
    Wcurve: {
      label: "Coeff. Curve",
      tooltip: "",
      input: "UniversalDropDown",
      entries: 'CURVES',
      section: ['Weir'],
      default: '*'
    },
    Description: {
      label: "Description",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    otherTables: ['XSECTIONS']
  },
  //==========XSECTIONS==============
  /*
[XSECTIONS]
;;Link           Shape        Geom1            Geom2      Geom3      Geom4      Barrels    Culvert   
;;-------------- ------------ ---------------- ---------- ---------- ---------- ---------- ----------
1                CIRCULAR     1                0          0          0          1                    
  */
  XSECTIONS: {
    Shape: {
      label: "Shape",
      tooltip: "",
      input: "UniversalDropDown",
      entries: XsectTypeWords,
      default: 'CIRCULAR'
    },
    Geom1: {
      label: "Max. Depth",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Geom2: {
      label: "Geom2",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Geom3: {
      label: "Geom3",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Geom4: {
      label: "Geom4",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Barrels: {
      label: "Barrels",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
    Culvert: {
      label: "Culvert",
      tooltip: "",
      input: "UniversalInput",
      default: '0'
    },
  },
  //==========LOSSES==============
  LOSSES: {
    Kin: {
      label: "Entry Loss Coeff.",
      tooltip: "Coeff. for energy losses at the entrance of the conduit",
      input: "UniversalInput",
      default: '0'
    },
    Kout: {
      label: "Exit Loss Coeff.",
      tooltip: "Coeff. for energy losses at the exit of the conduit",
      input: "UniversalInput",
      default: '0'
    },
    Kavg: {
      label: "Avg. Loss Coeff.",
      tooltip: "Coeff. for energy losses along the length of the conduit",
      input: "UniversalInput",
      default: '0'
    },
    Seepage: {
      label: "Seepage Rate",
      tooltip: "Rate of seepage loss into surrounding soil",
      input: "UniversalInput",
      default: '0'
    },
    Flap: {
      label: "Flap Gate",
      tooltip: "Select YES if a flap gate prevents reverse flow through a conduit",
      input: "UniversalDropDown",
      entries: ['NO', 'YES'],
      default: 'NO'
    },
  },
  //==========SUBCATCHMENTS==============
  SUBCATCHMENTS: {
      RainGage: {
        label: "Rain Gage",
        tooltip: "",
        input: "UniversalDropDown",
        entries: 'RAINGAGES',
        section: [],
        default: 'none'
      },
      Outlet: {
        label: "Outlet Node",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      Area: {
        label: "Area",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      Width: {
        label: "Width",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      PctSlope: {
        label: "% Slope",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      PctImperv: {
        label: "% Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      CurbLen: {
        label: "Curb Length",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      SnowPack: {
        label: "Snow Pack",
        tooltip: "",
        input: "UniversalDropDown",
        entries: 'SNOWPACKS',
        section: [],
        default: '*'
      },
      InfiltrationLink: {
        label: "Infiltration",
        tooltip: "Subcatchment infiltration",
        input: "Button",
        clickEffect: 'Infiltration',
        tableName: 'INFILTRATION',
        canClear: true,
        default: ''
      },
      GroundwaterLink: {
        label: "Groundwater",
        tooltip: "Subcatchment groundwater",
        input: "Button",
        clickEffect: 'Groundwater',
        tableName: 'GROUNDWATER',
        canClear: true,
        default: ''
      },
      LIDKeysLink: {
        label: "LID Controls",
        tooltip: "Subcatchment LID controls",
        input: "Button",
        clickEffect: 'LIDKeys',
        default: ''
      },
      LandUsesLink: {
        label: "Land Uses",
        tooltip: "Subcatchment land uses",
        input: "Button",
        clickEffect: 'Coverages',
        default: ''
      },
      InitialBuildupLink: {
        label: "Initial Buildup",
        tooltip: "Subcatchment initial buildup",
        input: "Button",
        clickEffect: 'Loadings',
        default: ''
      },
      PatternsLink: {
        label: "Patterns",
        tooltip: "Subcatchment patterns",
        input: "Button",
        clickEffect: 'SubcatchmentPatterns',
        default: ''
      },
      Description: {
        label: "Description",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      otherTables: ['SUBAREAS'/*,'INFILTRATION'*/]
    },
    //==========SUBAREAS==============
    SUBAREAS: {
      NImperv: {
        label: "N-Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      NPerv: {
        label: "N-Perv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      SImperv: {
        label: "Dstore-Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      SPerv: {
        label: "Dstore-Perv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      PctZero: {
        label: "%Zero-Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      RouteTo: {
        label: "Subarea Routing",
        tooltip: "",
        input: "UniversalDropDown",
        entries: ['OUTLET', 'IMPERVIOUS', 'PERVIOUS'],
        default: 'OUTLET'
      },
      PctRouted: {
        label: "Percent Routed",
        tooltip: "",
        input: "UniversalInput",
        default: '100'
      },
    },
    //==========INFILTRATION==============
    INFILTRATION: {
      NImperv: {
        label: "N-Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      NPerv: {
        label: "N-Perv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      SImperv: {
        label: "Dstore-Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      SPerv: {
        label: "Dstore-Perv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      PctZero: {
        label: "%Zero-Imperv",
        tooltip: "",
        input: "UniversalInput",
        default: '0'
      },
      RouteTo: {
        label: "Subarea Routing",
        tooltip: "",
        input: "UniversalDropDown",
        entries: ['OUTLET', 'IMPERVIOUS', 'PERVIOUS'],
        default: 'OUTLET'
      },
      PctRouted: {
        label: "Percent Routed",
        tooltip: "",
        input: "UniversalInput",
        default: '1000'
      },
    }
  }

  export default SwmmInpFormat